/**
 * Valore di carico di un trasferimento in entrata (F-174).
 *
 * Un trasferimento non muove cassa, quindi l'importo del movimento e' zero; ma
 * quella quantita' era costata qualcosa, altrove. Senza un valore di carico
 * entra a costo zero e il P&L della posizione e' l'intero valore di mercato.
 *
 * Il sistema propone il prezzo dell'asset al momento della ricezione; qui
 * l'utente scrive quello che ha pagato davvero, oppure torna all'automatico.
 */

import { useState, type FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDateTime, formatQuantity } from '@/lib/format';
import { Alert, Button, Field, Modal } from '@/ui';
import { useUpdateCostBasis, type PortfolioTransaction } from './api';
import { normalizeAmountInput } from './cost-basis-input';

export function CostBasisModal({
  portfolioId,
  transaction,
  onClose,
}: {
  portfolioId: string;
  transaction: PortfolioTransaction;
  onClose: () => void;
}) {
  const mutation = useUpdateCostBasis(portfolioId);
  const [value, setValue] = useState(transaction.costBasis ?? '');
  const [invalid, setInvalid] = useState(false);

  const save = (costBasis: string | null) =>
    mutation.mutate({ transactionId: transaction.id, costBasis }, { onSuccess: onClose });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeAmountInput(value);
    setInvalid(normalized === null);
    if (normalized !== null) save(normalized);
  };

  return (
    <Modal title="Valore di carico" onClose={onClose}>
      <form className="cost-basis-form" onSubmit={onSubmit}>
        <p>
          {formatQuantity(transaction.quantity ?? '0')} {transaction.instrument?.symbol ?? transaction.instrument?.name}{' '}
          arrivati il {formatDateTime(transaction.txDate)}. Il valore di carico è quanto pesano nel costo medio della
          posizione, e quindi nel P&amp;L: non muove la liquidità.
        </p>
        <p className="cell--minor">
          {transaction.costBasisSource === 'manual'
            ? 'Valore scritto da te.'
            : transaction.costBasisSource === 'market'
              ? `Valore calcolato al prezzo di mercato della ricezione: ${formatCurrency(transaction.costBasis, transaction.currency)}.`
              : 'Nessun valore: il trasferimento entra a costo zero.'}
        </p>

        <Field
          label={`Valore di carico totale (${transaction.currency})`}
          value={value}
          numeric
          autoFocus
          onChange={(event) => {
            setValue(event.target.value);
            setInvalid(false);
          }}
          error={invalid ? 'Un importo positivo, al massimo 4 decimali' : undefined}
          hint="Quanto l'avevi pagato, in totale, prima di trasferirlo qui."
        />

        {mutation.isError && (
          <Alert variant="error">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Salvataggio non riuscito'}
          </Alert>
        )}

        <div className="cost-basis-form__actions">
          {transaction.costBasisSource === 'manual' && (
            <Button type="button" variant="ghost" onClick={() => save(null)} disabled={mutation.isPending}>
              Torna al valore automatico
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Annulla
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvataggio…' : 'Salva'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
